import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { buildNavSections, isNavItemActive } from '../../navigation/buildNavSections';
import { SIDEBAR_ACTIONS } from '../../navigation/navConfig';
import { requestWalkthroughReplay } from '../../features/walkthrough/walkthroughOrchestrator';
import { useWalkthrough } from '../../features/walkthrough/walkthroughContext';
import { useNavUi } from './navUiContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { getDefaultDashboardPath, resolvePrimaryRole } from '../../utils/rolePriority';
import SidebarNavSection from './SidebarNavSection';
import BrandLogo from './BrandLogo';

export default function RoleSidebar({
  user,
  mode,
  collapsed,
  mobileDrawerOpen,
  onToggleCollapsed,
  onCloseMobileDrawer,
  onAction,
  pendingActionId = null,
  drawerFooter,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isWalkthroughEnabled } = useFeatureFlags();
  const sections = useMemo(
    () => buildNavSections(user, { walkthroughEnabled: isWalkthroughEnabled }),
    [isWalkthroughEnabled, user]
  );
  const { startTour, endActiveTour } = useWalkthrough();
  const { openMobileNav, isMobile } = useNavUi();

  const handleSelect = (item) => {
    if (item.type === 'action' && item.actionId === SIDEBAR_ACTIONS.APP_TOUR) {
      if (isWalkthroughEnabled) {
        void requestWalkthroughReplay({
          user,
          navigate,
          startTour,
          endActiveTour,
          isMobile: isMobile || mode === 'mobile',
          openMobileNav,
        });
      }
      if (mode === 'mobile') {
        onCloseMobileDrawer();
      }
      return;
    }

    if (item.type === 'action' && item.actionId) {
      onAction(item.actionId);
    }
    if (mode === 'mobile') {
      onCloseMobileDrawer();
    }
  };

  const isNavCollapsed = mode !== 'mobile' && collapsed;
  const showFullBrand = mode === 'desktop' && !collapsed;
  const homePath = useMemo(
    () => getDefaultDashboardPath(resolvePrimaryRole(user)),
    [user]
  );

  const brandLink = (
    <Link
      to={homePath}
      className={cn(
        'rounded-lg outline-none transition-colors duration-200 ease-in-out motion-reduce:transition-none',
        'hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-gray-800/60',
        showFullBrand ? 'block min-w-0 w-full px-1 py-1' : 'mx-auto flex w-fit items-center justify-center p-1'
      )}
      aria-label={isNavCollapsed ? 'KadelLabs home' : undefined}
    >
      <BrandLogo
        animate
        expanded={showFullBrand}
        showTagline={showFullBrand}
        className={showFullBrand ? 'min-w-0' : undefined}
      />
    </Link>
  );

  const sidebarBrand = (
    <div
      data-testid="sidebar-brand"
      className={cn(
        'sidebar-brand shrink-0 border-b border-gray-200 dark:border-gray-700',
        'header-safe safe-area-left safe-area-right',
        showFullBrand ? 'px-2 py-1.5 max-md:px-1.5 max-md:py-1 md:py-2' : 'flex min-h-[44px] max-h-[var(--header-h)] items-center justify-center px-0.5 py-1 max-md:py-0.5'
      )}
    >
      {isNavCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{brandLink}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} collisionPadding={16} className="z-[150] dark:bg-gray-800 dark:border-gray-700">
            KadelLabs
          </TooltipContent>
        </Tooltip>
      ) : (
        brandLink
      )}
    </div>
  );

  const navBody = (
    <nav
      className="flex flex-1 min-h-0 flex-col space-y-1.5 overflow-y-auto overscroll-contain px-1 py-0.5 max-md:space-y-1 max-md:px-0.5 md:space-y-2 md:px-1.5 md:py-1 lg:space-y-3 lg:px-2 lg:py-1.5"
      aria-label="Main navigation"
      data-walkthrough="nav-sections"
    >
      {sections.map((section) => (
        <SidebarNavSection
          key={section.id}
          section={section}
          pathname={location.pathname}
          collapsed={isNavCollapsed}
          onSelect={handleSelect}
          isActive={isNavItemActive}
          pendingActionId={pendingActionId}
        />
      ))}
    </nav>
  );

  const sidebarRail = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {isNavCollapsed ? (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          {sidebarBrand}
          {navBody}
        </TooltipProvider>
      ) : (
        <>
          {sidebarBrand}
          {navBody}
        </>
      )}
    </div>
  );

  const collapseToggle =
    mode === 'desktop' ? (
      <div className={cn('flex shrink-0 border-t border-gray-200 p-1.5 dark:border-gray-700', collapsed ? 'justify-center' : 'justify-end')}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          data-walkthrough="nav-sidebar-toggle"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>
    ) : null;

  if (mode === 'mobile') {
    return (
      <Sheet open={mobileDrawerOpen} onOpenChange={(open) => !open && onCloseMobileDrawer()}>
        <SheetContent
          side="left"
          className="w-[min(calc(100vw-env(safe-area-inset-left,0px)-env(safe-area-inset-right,0px)-1rem),350px)] h-[100dvh] max-h-[100dvh] min-h-0 p-0 flex flex-col overflow-hidden dark:bg-gray-900 dark:border-gray-700 safe-area-left [&>button]:hidden"
        >
          <SheetHeader className="shrink-0 flex-row items-center justify-between gap-2 overflow-hidden border-b p-3 max-md:gap-1.5 max-md:p-2 dark:border-gray-700 header-safe safe-area-right">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Link
              to={homePath}
              className="min-w-0 flex-1 rounded-lg outline-none transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-gray-800/60"
              onClick={onCloseMobileDrawer}
            >
              <BrandLogo variant="full" className="min-w-0" />
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0"
              onClick={onCloseMobileDrawer}
              aria-label="Close navigation"
            >
              <X className="h-4 w-4" />
            </Button>
          </SheetHeader>
          {navBody}
          {drawerFooter ? (
            <div className="shrink-0 space-y-2 border-t p-3 safe-area-bottom drawer-safe max-md:space-y-1.5 max-md:p-2 md:space-y-3 dark:border-gray-700">
              {drawerFooter}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className={cn(
        'relative z-[90] flex h-full max-h-full min-h-0 shrink-0 flex-col self-stretch border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
        'transition-[width] duration-200 ease-in-out motion-reduce:transition-none overflow-hidden w-[var(--sidebar-w,216px)]'
      )}
      aria-label="Sidebar navigation"
      data-walkthrough="nav-sidebar"
    >
      {sidebarRail}
      {collapseToggle}
    </aside>
  );
}

export { SIDEBAR_ACTIONS };
